import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { Workspace } from '@/api/generated/schemas.ts';
import { useDeleteWorkspaceLogo, useUpdateWorkspace, useUploadWorkspaceLogo } from '@/api/generated/workspaces.ts';
import CopyField from '@/core/components/CopyField.tsx';
import Dialog from '@/core/components/Dialog.tsx';
import ImageField, { type ImageChange, saveImageChange } from '@/core/components/ImageField.tsx';
import { zodResolver } from '@hookform/resolvers/zod';

import TermsFormFields from '../forms/TermsFormFields.tsx';
import WorkspaceFormFields, {
    type WorkspaceFormOutput,
    type WorkspaceFormValues,
    getWorkspaceFormDefaults,
    toWorkspaceInput,
    workspaceFormSchema,
} from '../forms/WorkspaceFormFields.tsx';
import { useCreateWorkspaceForm } from '../useCreateWorkspaceForm.ts';

const MAX_LOGO_SIZE = 2 * 1024 * 1024;

// Edits the workspace, or creates a new one when it's null.
export interface WorkspaceDialogTarget {
    workspace: Workspace | null;
}

interface CreateWorkspaceFormProps {
    onClose: () => unknown;
    onSaved?: (workspace: Workspace) => void;
}

const CreateWorkspaceForm = ({ onClose, onSaved }: CreateWorkspaceFormProps) => {
    const { t } = useTranslation();
    const { form, handleSubmit } = useCreateWorkspaceForm(async (saved) => {
        await onClose();
        onSaved?.(saved);
    });

    return (
        <Dialog.Form
            form={form}
            onSubmit={handleSubmit}
            title={t('workspaces.newTitle')}
            description={t('workspaces.newHint')}
            submitLabel={t('common.create')}
            onCancel={onClose}
        >
            <div className="flex flex-col gap-6">
                <WorkspaceFormFields hasAutoSlug />
                <TermsFormFields />
            </div>
        </Dialog.Form>
    );
};

interface EditWorkspaceFormProps {
    workspace: Workspace;
    onClose: () => unknown;
    onSaved?: (workspace: Workspace) => void;
}

const EditWorkspaceForm = ({ workspace, onClose, onSaved }: EditWorkspaceFormProps) => {
    const { t } = useTranslation();
    const updateWorkspace = useUpdateWorkspace();
    const uploadLogo = useUploadWorkspaceLogo();
    const deleteLogo = useDeleteWorkspaceLogo();
    const [logoChange, setLogoChange] = useState<ImageChange>(undefined);
    const form = useForm<WorkspaceFormValues, unknown, WorkspaceFormOutput>({
        resolver: zodResolver(workspaceFormSchema),
        defaultValues: getWorkspaceFormDefaults(workspace),
    });
    const [name, colorHue] = useWatch({ control: form.control, name: ['name', 'color_hue'] });

    // The logo is uploaded on its own (plan task 29), once the workspace is saved. Its endpoints are the open
    // workspace's, which is the one edited here (WorkspaceDetailsSection).
    const handleSubmit = async (values: WorkspaceFormOutput) => {
        const stored = await updateWorkspace.mutateAsync({ workspaceId: workspace.id, data: toWorkspaceInput(values) });
        const saved = await saveImageChange(logoChange, stored, stored.logo_url !== null, {
            upload: (file) => uploadLogo.mutateAsync({ data: { file } }),
            remove: () => deleteLogo.mutateAsync(),
        });
        await onClose();
        onSaved?.(saved);
    };

    return (
        <Dialog.Form
            form={form}
            onSubmit={handleSubmit}
            title={t('workspaces.editTitle')}
            submitLabel={t('common.save')}
            onCancel={onClose}
        >
            <div className="flex flex-col gap-4">
                <WorkspaceFormFields />
                <ImageField
                    label={t('workspaces.fields.logo')}
                    chooseLabel={t('workspaces.logo.choose')}
                    hint={t('workspaces.logo.hint')}
                    avatarName={name}
                    hue={colorHue}
                    src={workspace.logo_url}
                    change={logoChange}
                    onChange={setLogoChange}
                    maxSize={MAX_LOGO_SIZE}
                />
                <CopyField label={t('workspaces.fields.id')} value={workspace.id} helpText={t('workspaces.idHint')} />
            </div>
        </Dialog.Form>
    );
};

interface WorkspaceDialogProps {
    // null keeps the dialog closed.
    target: WorkspaceDialogTarget | null;
    onClose: () => unknown;
    onSaved?: (workspace: Workspace) => void;
}

const WorkspaceDialog = ({ target, onClose, onSaved }: WorkspaceDialogProps) => (
    <Dialog isOpen={target !== null} onClose={onClose} size="md">
        {target &&
            (target.workspace ? (
                <EditWorkspaceForm workspace={target.workspace} onClose={onClose} onSaved={onSaved} />
            ) : (
                <CreateWorkspaceForm onClose={onClose} onSaved={onSaved} />
            ))}
    </Dialog>
);

export default WorkspaceDialog;
